import React, { useState } from 'react';
import { Plus, Play, Pencil, Trash2, ChevronLeft, ChevronRight, Images } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/services/api';
import GalleryPostEditor from '@/components/GalleryPostEditor';

function mediaList(post) {
  return [...(post.media || [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
}
function preview(post) {
  return (post.caption && post.caption.trim()) || (post.event_name && post.event_name.trim()) || '';
}

// Single media tile (image, or video showing first frame + play overlay).
function MediaTile({ m, className }) {
  if (m.type === 'video') {
    return (
      <div className={`relative ${className || ''}`}>
        <video src={m.url} className="w-full h-full object-cover" muted playsInline preload="metadata" />
        <span className="absolute inset-0 flex items-center justify-center">
          <Play className="h-8 w-8 text-white drop-shadow" />
        </span>
      </div>
    );
  }
  return <img src={m.url} alt="" className={`object-cover ${className || ''}`} loading="lazy" />;
}

// Modal viewer: single media, or a carousel when the post has multiple.
function PostViewer({ post, onClose }) {
  const media = mediaList(post);
  const [idx, setIdx] = useState(0);
  const multiple = media.length > 1;
  const cur = media[Math.min(idx, media.length - 1)] || media[0];
  const go = (d) => setIdx((i) => (i + d + media.length) % media.length);

  return (
    <Dialog open={!!post} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden">
        <div className="relative bg-black flex items-center justify-center" style={{ minHeight: 240 }}>
          {cur && (cur.type === 'video' ? (
            // Autoplay muted when the modal opens; remounts per slide via key.
            <video key={cur.url} src={cur.url} className="max-h-[70vh] w-full object-contain bg-black"
              autoPlay muted loop playsInline controls />
          ) : (
            <img key={cur.url} src={cur.url} alt="" className="max-h-[70vh] w-full object-contain bg-black" />
          ))}
          {multiple && (
            <>
              <button type="button" onClick={() => go(-1)} aria-label="Previous"
                className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-1.5 text-white">
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button type="button" onClick={() => go(1)} aria-label="Next"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-1.5 text-white">
                <ChevronRight className="h-5 w-5" />
              </button>
              <span className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-2 py-0.5 text-xs text-white">
                {Math.min(idx, media.length - 1) + 1} / {media.length}
              </span>
            </>
          )}
        </div>
        {(post.event_name || post.caption) && (
          <div className="p-4 space-y-1">
            {post.event_name && <div className="font-display font-semibold">{post.event_name}</div>}
            {post.caption && <p className="text-sm text-foreground/90 whitespace-pre-wrap">{post.caption}</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Gallery tab content.
 *  - posts:    array (public view, or owner view with ids when isOwner)
 *  - isOwner:  show Add/Edit/Delete + empty state
 *  - onChanged: () => void  (refetch after a create/edit/delete)
 */
export default function DJGalleryTab({ posts = [], isOwner = false, onChanged }) {
  const [viewer, setViewer] = useState(null);     // post being viewed
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorPost, setEditorPost] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const openAdd = () => { setEditorPost(null); setEditorOpen(true); };
  const openEdit = (post) => { setEditorPost(post); setEditorOpen(true); };

  const del = async (post) => {
    if (!post?.id) return;
    if (!window.confirm('Delete this post permanently? This cannot be undone.')) return;
    setDeletingId(post.id);
    try {
      await api.del(`/users/me/dj-gallery/${post.id}`);
      toast.success('Post deleted');
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    } finally { setDeletingId(null); }
  };

  const atMax = isOwner && posts.length >= 20;

  return (
    <div className="space-y-4">
      {isOwner && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">{posts.length}/20 posts</span>
          <Button size="sm" onClick={openAdd} disabled={atMax}>
            <Plus className="h-4 w-4 mr-1" /> Add Post
          </Button>
        </div>
      )}

      {posts.length === 0 ? (
        <div className="py-12 text-center text-muted-foreground">
          {isOwner ? (
            <>
              <Images className="mx-auto mb-3 h-10 w-10 opacity-40" />
              <p className="mb-4">Add your first gallery post.</p>
              <Button size="sm" onClick={openAdd}><Plus className="h-4 w-4 mr-1" /> Add Post</Button>
            </>
          ) : (
            <p>No gallery posts yet.</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {posts.map((post, i) => {
            const media = mediaList(post);
            const first = media[0];
            const cap = preview(post);
            return (
              <div key={post.id || i} className="group relative">
                <button type="button" onClick={() => setViewer(post)}
                  className="block w-full aspect-square overflow-hidden rounded-md bg-muted">
                  {first && <MediaTile m={first} className="w-full h-full" />}
                  {media.length > 1 && (
                    <span className="absolute top-1 right-1 rounded bg-black/60 px-1 py-0.5 text-[10px] text-white flex items-center gap-0.5">
                      <Images className="h-3 w-3" /> {media.length}
                    </span>
                  )}
                </button>
                <p className="mt-1 text-xs text-muted-foreground truncate">{cap || ' '}</p>

                {isOwner && post.id && (
                  <div className="absolute bottom-6 left-1 flex gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                    <button type="button" onClick={() => openEdit(post)} aria-label="Edit post"
                      className="rounded bg-black/60 p-1 text-white"><Pencil className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => del(post)} aria-label="Delete post"
                      disabled={deletingId === post.id}
                      className="rounded bg-black/60 p-1 text-white disabled:opacity-40"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {viewer && <PostViewer post={viewer} onClose={() => setViewer(null)} />}
      {isOwner && (
        <GalleryPostEditor open={editorOpen} onOpenChange={setEditorOpen} post={editorPost}
          onSaved={() => { setEditorOpen(false); onChanged && onChanged(); }} />
      )}
    </div>
  );
}
